// ** Modules
import React from 'react';

// ** UI
import MuiSwipeableDrawer, { type SwipeableDrawerProps } from '@mui/material/SwipeableDrawer';
import { useTheme, styled } from '@mui/material/styles';

// ** Types
import type { LayoutProps } from '@/types/layoutProps';

type VerticalLayout = NonNullable<LayoutProps['verticalLayoutProps']>;
type NavMenu = NonNullable<VerticalLayout['navMenu']>;
type CustomDrawerProps = {
	navWidth: number;
	navHover: boolean;
	navVisible: boolean;
	collapsedNavWidth: number;
	navigationBorderWidth: number;
	hidden: LayoutProps['hidden'];
	settings: LayoutProps['settings'];
	children: LayoutProps['children'];
	setNavHover: (values: boolean) => void;
	setNavVisible: (value: boolean) => void;
	navMenuProps: NavMenu['componentProps'];
};

// ** Styled Components
const SwipeableDrawer = styled(MuiSwipeableDrawer)<SwipeableDrawerProps>({
	overflowX: 'hidden',
	transition: 'width .25s ease-in-out',
	'& ul': {
		listStyle: 'none'
	},
	'& .MuiListItem-gutters': {
		paddingLeft: 4,
		paddingRight: 4
	},
	'& .MuiDrawer-paper': {
		left: 'unset',
		right: 'unset',
		overflowX: 'hidden',
		transition: 'width .25s ease-in-out, box-shadow .25s ease-in-out'
	}
});

// const drawerWidth: number = 240;

// const CustomDrawer = ({
// 	hidden,
// 	children,
// 	navHover,
// 	navWidth,
// 	settings,
// 	navVisible,
// 	setNavHover,
// 	navMenuProps,
// 	setNavVisible,
// 	collapsedNavWidth,
// 	navigationBorderWidth
// }: CustomDrawerProps) => {
// 	// Hooks
// 	const theme = useTheme();

// 	const { navcollapsed } = settings;

// 	let flag = true;

// 	// Drawer Props for Mobile & Tablet screens
// 	const MobileDrawerProps = {
// 		open: navVisible,
// 		onOpen: () => setNavVisible(true),
// 		onClose: () => setNavVisible(false),
// 		ModalProps: {
// 			keepMounted: true // Better open performance on mobile.
// 		}
// 	};

// 	// Drawer Props for Desktop screens
// 	const DesktopDrawerProps = {
// 		open: true,
// 		onOpen: () => null,
// 		onClose: () => null,
// 		onMouseEnter: () => {
// 			// Declared flag to resolve first time flicker issue while trying to collapse the menu
// 			if (flag || navcollapsed) {
// 				setNavHover(true);
// 				flag = false;
// 			}
// 		},
// 		onMouseLeave: () => {
// 			if (navcollapsed) {
// 				setNavHover(false);
// 			}
// 		}
// 	};

// 	let userNavMenuStyle = {};
// 	let userNavMenuPaperStyle = {};

// 	if (navMenuProps?.sx) {
// 		userNavMenuStyle = navMenuProps.sx;
// 	}
// 	if (navMenuProps?.PaperProps?.sx) {
// 		userNavMenuPaperStyle = navMenuProps.PaperProps.sx;
// 	}

// 	const userNavMenuProps = Object.assign({}, navMenuProps);
// 	delete userNavMenuProps.sx;
// 	delete userNavMenuProps.PaperProps;

// 	return (
// 		<SwipeableDrawer
// 			className="layout-vertical-nav"
// 			variant={hidden ? 'temporary' : 'permanent'}
// 			{...(hidden ? { ...MobileDrawerProps } : { ...DesktopDrawerProps })}
// 			PaperProps={{
// 				sx: {
// 					backgroundColor: 'background.default',
// 					boxShadow: '5px 0px 10px rgba(0, 0, 0, 0.1);',
// 					width: navcollapsed && !navHover ? collapsedNavWidth : navWidth,
// 					// ...(!hidden && navcollapsed && navHover ? { boxShadow: 9 } : {}), // ? To check what effect was doing this line
// 					borderRight:
// 						navigationBorderWidth === 0
// 							? 0
// 							: `${navigationBorderWidth}px solid ${theme.palette.divider}`,
// 					...userNavMenuPaperStyle
// 				},
// 				...navMenuProps?.PaperProps
// 			}}
// 			sx={{
// 				width: navcollapsed ? collapsedNavWidth : navWidth,
// 				...userNavMenuStyle
// 			}}
// 			{...userNavMenuProps}
// 		>
// 			{children}
// 		</SwipeableDrawer>
// 	);
// };

const CustomDrawer = ({
	hidden,
	children,
	navHover,
	navWidth,
	settings,
	navVisible,
	setNavHover,
	navMenuProps,
	setNavVisible,
	collapsedNavWidth,
	navigationBorderWidth
}: CustomDrawerProps) => {
	const theme = useTheme();
	const { navcollapsed } = settings;

	// Use a ref for the flag to avoid re-renders or stale closures
	const flag = React.useRef(true);

	const MobileDrawerProps = {
		open: navVisible,
		onOpen: () => setNavVisible(true),
		onClose: () => setNavVisible(false),
		slotProps: {
			backdrop: {
				// Equivalent to ModalProps keepMounted
				sx: { keepMounted: true }
			}
		}
	};

	const DesktopDrawerProps = {
		open: true,
		onOpen: () => null,
		onClose: () => null,
		onMouseEnter: () => {
			if (flag.current || navcollapsed) {
				setNavHover(true);
				flag.current = false;
			}
		},
		onMouseLeave: () => {
			if (navcollapsed) {
				setNavHover(false);
			}
		}
	};

	// 1. Cast the whole props object to bypass strict v9 checks for legacy props
	const navMenuPropsAsAny = navMenuProps as any;

	// 2. Extract the Root SX style (for the container)
	const userNavMenuStyle = navMenuProps?.sx || {};

	// 3. Extract the Paper SX style (handling both v9 slot and legacy PaperProps)
	const userNavMenuPaperStyle = (navMenuProps?.slotProps?.paper as any)?.sx || navMenuPropsAsAny?.PaperProps?.sx || {};

	// 4. Clean destructuring to remove properties that cause errors when passed to SwipeableDrawer
	const {
		sx: _unusedSx,
		PaperProps: _unusedPaper,
		slotProps: _unusedSlot,
		...userNavMenuProps
	} = navMenuPropsAsAny || {};

	return (
		<SwipeableDrawer
			className="layout-vertical-nav"
			variant={hidden ? 'temporary' : 'permanent'}
			{...(hidden ? MobileDrawerProps : DesktopDrawerProps)}
			// MUI v9 Slot Pattern
			slotProps={{
				paper: {
					// By using the 'sx' key inside 'paper', we satisfy the SlotProps requirements
					sx: {
						backgroundColor: 'background.default',
						boxShadow: '5px 0px 10px rgba(0, 0, 0, 0.1)',
						width: navcollapsed && !navHover ? collapsedNavWidth : navWidth,
						overflowX: 'hidden',
						borderRight: navigationBorderWidth === 0 ? 0 : `${navigationBorderWidth}px solid ${theme.palette.divider}`,
						...userNavMenuPaperStyle,
						// Ensure the transition is applied to the paper slot
						transition: theme.transitions.create(['width', 'box-shadow'], {
							easing: theme.transitions.easing.easeInOut,
							duration: theme.transitions.duration.standard
						})
					}
				}
			}}
			sx={{
				width: navcollapsed ? collapsedNavWidth : navWidth,
				// We keep the transition here for the container itself
				transition: theme.transitions.create('width', {
					easing: theme.transitions.easing.easeInOut,
					duration: theme.transitions.duration.standard
				}),
				...userNavMenuStyle
			}}
			{...userNavMenuProps}
		>
			{children}
		</SwipeableDrawer>
	);
};

export default CustomDrawer;
